import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  Header,
  ScreenLayout,
  StepperReturnBanner,
  colors,
  spacing,
  typography,
} from '../components';
import { BusinessUserPicker } from '../components/BusinessUserPicker';
import { Modal } from '../components/Modal';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { apiService, BusinessUser } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ConnectedPhoneAccount {
  provider: string;
  callerNumber?: string;
  status: 'active' | 'inactive' | 'invalid';
  connectedAt: string;
}

interface DiscoveredAgent {
  extensionNumber: string;
  status: string;
  linkedUserId: string | null;
}

/**
 * Phone channel connect flow. InnoCalls authenticates its REST "API Gateway"
 * via HTTP Basic (apiId:apiSecret) and its client-side WebRTC SDK via an
 * account-wide apiKey — this is a credential-paste form (dashboard: Developer
 * > API Gateway Credentials tab + WebRTC tab), not an OAuth callback.
 *
 * Admin/owner only. Once the business account is connected, this screen
 * discovers every extension already provisioned on the InnoCalls account
 * (GET /api/phone/agents/discover) and lets the admin assign each one to a
 * business user (POST /api/phone/agents/link) — replacing the earlier
 * self-service "enable calling for me" flow, since agents no longer create
 * their own extensions.
 */
export const PhoneSetupScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo, setActiveTab, activeTab, routeParams } = useAppContext();
  // This screen has no natural "setup complete" moment (it's a persistent management screen,
  // not a linear wizard) — when embedded in the channel onboarding stepper, add an explicit
  // Continue/Skip footer instead of trying to infer completion from its internal state.
  const onCompleteScreen = (routeParams as any)?.onCompleteScreen;
  const onCompleteParams = (routeParams as any)?.onCompleteParams;
  const isEmbeddedInStepper = !!onCompleteScreen;
  const { businessRole, currentBusiness } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const isAdminOrOwner = businessRole === 'admin' || businessRole === 'owner';

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [account, setAccount] = useState<ConnectedPhoneAccount | null>(null);
  const [showLeftPane, setShowLeftPane] = useState(Platform.OS === 'web');

  const [apiId, setApiId] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [callerNumber, setCallerNumber] = useState('');

  const [agents, setAgents] = useState<DiscoveredAgent[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [businessUsers, setBusinessUsers] = useState<BusinessUser[]>([]);

  const [assigningExtension, setAssigningExtension] = useState<string | null>(null);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  // BusinessUserPicker (single mode) calls onConfirm() then onClose()
  // synchronously in the same handler — both fire within the same render
  // cycle, so onClose's closure would otherwise see showPinModal's stale
  // (pre-update) value and wrongly conclude no pick happened. This ref is
  // set synchronously inside handleUserPicked, before onClose runs, so
  // onClose can read the CURRENT outcome instead of a stale render's state.
  const justPickedUserRef = useRef(false);

  // Re-runs on every mount, including after a hard reload (e.g. the full
  // page reload theme-switching triggers, see ThemeContext.setThemeMode) —
  // currentBusiness/businessRole are rehydrated from persisted storage by
  // the app store before this screen mounts, so a fresh fetch here is
  // always correct rather than relying on any now-stale in-memory state.
  useEffect(() => {
    setActiveTab('home');
    if (isAdminOrOwner) {
      loadAccount();
    }
  }, [isAdminOrOwner]);

  const loadAccount = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiService.getPhoneAccount();
      const acc = res?.data ?? null;
      setAccount(acc);
      if (acc) {
        loadAgents();
        loadBusinessUsers();
      }
    } catch (err) {
      console.warn('[PhoneSetup] loadAccount error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadAgents = useCallback(async () => {
    setIsLoadingAgents(true);
    try {
      const res = await apiService.discoverPhoneAgents();
      setAgents(Array.isArray(res?.data) ? res.data : []);
    } catch (err: any) {
      console.warn('[PhoneSetup] loadAgents error:', err);
      showNotification('error', t('phoneSetup.discoveryFailedTitle'), err.message ?? t('phoneSetup.discoveryFailedMessage'));
    } finally {
      setIsLoadingAgents(false);
    }
  }, [showNotification, t]);

  const loadBusinessUsers = useCallback(async () => {
    if (!currentBusiness?._id) return;
    try {
      const res = await apiService.getBusinessUsers(currentBusiness._id);
      setBusinessUsers(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.warn('[PhoneSetup] loadBusinessUsers error:', err);
    }
  }, [currentBusiness?._id]);

  const getUserLabel = (userId: string | null): string => {
    if (!userId) return '';
    const bu = businessUsers.find((u) => u.userId === userId || u.user?._id === userId);
    if (!bu) return userId;
    const name = `${bu.user?.firstName ?? ''} ${bu.user?.lastName ?? ''}`.trim();
    return name || bu.user?.email || userId;
  };

  const handleConnect = async () => {
    if (!apiId.trim() || !apiSecret.trim() || !apiKey.trim()) {
      showNotification('warning', t('phoneSetup.missingFieldsTitle'), t('phoneSetup.missingFieldsMessage'));
      return;
    }

    setIsSaving(true);
    try {
      await apiService.connectPhoneAccount({
        provider: 'innocalls',
        providerConfig: {
          apiId: apiId.trim(),
          apiSecret: apiSecret.trim(),
          apiKey: apiKey.trim(),
          // Optional — only needed if/when automated campaign dialing
          // (single-call/init) is added; the interactive WebRTC call flow
          // doesn't use it. See project_innocalls_api memory for details.
          ...(campaignId.trim() ? { campaignId: campaignId.trim() } : {}),
        },
        callerNumber: callerNumber.trim() || undefined,
      });
      await loadAccount();
      setApiId('');
      setApiSecret('');
      setApiKey('');
      setCampaignId('');
      setCallerNumber('');
      showNotification('success', t('phoneSetup.connectedTitle'), t('phoneSetup.connectedMessage'));
    } catch (err: any) {
      console.error('[PhoneSetup] handleConnect error:', err);
      showNotification('error', t('phoneSetup.connectionFailedTitle'), err.message ?? t('phoneSetup.connectionFailedMessage'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setIsSaving(true);
    try {
      await apiService.disconnectPhoneAccount();
      setAccount(null);
      setAgents([]);
      showNotification('success', t('phoneSetup.disconnectedTitle'), t('phoneSetup.disconnectedMessage'));
    } catch (err: any) {
      showNotification('error', t('phoneSetup.errorTitle'), err.message ?? t('phoneSetup.disconnectFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // Assignment flow: pick extension -> pick user -> enter PIN -> link.
  const startAssign = (extensionNumber: string) => {
    justPickedUserRef.current = false;
    setAssigningExtension(extensionNumber);
    setShowUserPicker(true);
  };

  const handleUserPicked = (selectedIds: string[]) => {
    const userId = selectedIds[0];
    if (!userId) {
      setAssigningExtension(null);
      return;
    }
    justPickedUserRef.current = true;
    setPendingUserId(userId);
    setPinInput('');
    setShowPinModal(true);
  };

  const handleConfirmLink = async () => {
    if (!assigningExtension || !pendingUserId) return;
    if (pinInput.trim().length === 0) {
      showNotification('warning', t('phoneSetup.pinRequiredTitle'), t('phoneSetup.pinRequiredMessage'));
      return;
    }

    setIsLinking(true);
    try {
      await apiService.linkPhoneAgent({
        extensionNumber: assigningExtension,
        userId: pendingUserId,
        pin: pinInput.trim(),
      });
      showNotification('success', t('phoneSetup.agentLinkedTitle'), t('phoneSetup.agentLinkedMessage', { extension: assigningExtension }));
      setShowPinModal(false);
      setAssigningExtension(null);
      setPendingUserId(null);
      setPinInput('');
      await loadAgents();
    } catch (err: any) {
      console.error('[PhoneSetup] handleConfirmLink error:', err);
      showNotification('error', t('phoneSetup.assignmentFailedTitle'), err.message ?? t('phoneSetup.assignmentFailedMessage'));
    } finally {
      setIsLinking(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderConnected = () => (
    <View style={[styles.accountCard, { flexDirection: rtl.rowDirection }]}>
      <View style={[styles.accountInfo, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.accountAvatar, rtl.marginEnd(spacing.sm)]}>
          <Ionicons name="call" size={20} color={colors.white as string} />
        </View>
        <View>
          <Text style={[styles.accountName, { textAlign: rtl.textStart }]}>InnoCalls</Text>
          {account?.callerNumber && <Text style={[styles.accountSubtext, { textAlign: rtl.textStart }]}>{account.callerNumber}</Text>}
        </View>
      </View>
      <View style={[styles.accountActions, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.statusDot, { backgroundColor: account?.status === 'active' ? colors.status.success as string : colors.status.error as string }]} />
        <TouchableOpacity onPress={handleDisconnect} disabled={isSaving}>
          <Ionicons name="trash-outline" size={18} color={colors.status.error as string} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderForm = () => (
    <View style={styles.formCard}>
      <Text style={[styles.formLabel, { textAlign: rtl.textStart }]}>{t('phoneSetup.field.apiId')}</Text>
      <TextInput
        style={[styles.input, { textAlign: rtl.textStart }]}
        value={apiId}
        onChangeText={setApiId}
        placeholder={t('phoneSetup.field.apiIdPlaceholder')}
        placeholderTextColor={colors.text.secondary as string}
        autoCapitalize="none"
      />

      <Text style={[styles.formLabel, { textAlign: rtl.textStart }]}>{t('phoneSetup.field.apiSecret')}</Text>
      <TextInput
        style={[styles.input, { textAlign: rtl.textStart }]}
        value={apiSecret}
        onChangeText={setApiSecret}
        placeholder={t('phoneSetup.field.apiSecretPlaceholder')}
        placeholderTextColor={colors.text.secondary as string}
        autoCapitalize="none"
        secureTextEntry
      />

      <Text style={[styles.formLabel, { textAlign: rtl.textStart }]}>{t('phoneSetup.field.apiKey')}</Text>
      <TextInput
        style={[styles.input, { textAlign: rtl.textStart }]}
        value={apiKey}
        onChangeText={setApiKey}
        placeholder={t('phoneSetup.field.apiKeyPlaceholder')}
        placeholderTextColor={colors.text.secondary as string}
        autoCapitalize="none"
        secureTextEntry
      />

      <Text style={[styles.formLabel, { textAlign: rtl.textStart }]}>{t('phoneSetup.field.campaignId')}</Text>
      <TextInput
        style={[styles.input, { textAlign: rtl.textStart }]}
        value={campaignId}
        onChangeText={setCampaignId}
        placeholder={t('phoneSetup.field.campaignIdPlaceholder')}
        placeholderTextColor={colors.text.secondary as string}
        autoCapitalize="none"
      />

      <Text style={[styles.formLabel, { textAlign: rtl.textStart }]}>{t('phoneSetup.field.callerNumber')}</Text>
      <TextInput
        style={[styles.input, { textAlign: rtl.textStart }]}
        value={callerNumber}
        onChangeText={setCallerNumber}
        placeholder={t('phoneSetup.field.callerNumberPlaceholder')}
        placeholderTextColor={colors.text.secondary as string}
        keyboardType="phone-pad"
      />

      <TouchableOpacity
        style={[styles.connectButton, { flexDirection: rtl.rowDirection }, isSaving && styles.buttonDisabled]}
        onPress={handleConnect}
        disabled={isSaving}
      >
        {isSaving ? (
          <ActivityIndicator color={colors.white as string} />
        ) : (
          <>
            <Ionicons name="call" size={20} color={colors.white as string} />
            <Text style={styles.connectButtonText}>{t('phoneSetup.connectButton')}</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );

  const renderAgentsSection = () => (
    <View style={styles.formCard}>
      <View style={[styles.sectionHeaderRow, { flexDirection: rtl.rowDirection }]}>
        <View style={{ flex: 1 }}>
          <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('phoneSetup.agentsSectionTitle')}</Text>
          <Text style={[styles.sectionSubtitle, { textAlign: rtl.textStart }]}>{t('phoneSetup.agentsSectionSubtitle')}</Text>
        </View>
        <TouchableOpacity onPress={loadAgents} disabled={isLoadingAgents} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="refresh" size={20} color={colors.primary as string} />
        </TouchableOpacity>
      </View>

      {isLoadingAgents ? (
        <ActivityIndicator color={colors.primary as string} style={{ marginVertical: spacing.md }} />
      ) : agents.length === 0 ? (
        <Text style={[styles.emptyAgentsText, { textAlign: rtl.textStart }]}>{t('phoneSetup.noAgentsFound')}</Text>
      ) : (
        agents.map((agent) => (
          <View key={agent.extensionNumber} style={[styles.agentRow, { flexDirection: rtl.rowDirection }]}>
            <View style={styles.agentInfo}>
              <Text style={[styles.agentExtension, { textAlign: rtl.textStart }]}>{t('phoneSetup.extensionLabel', { extension: agent.extensionNumber })}</Text>
              {agent.linkedUserId ? (
                <Text style={[styles.agentLinkedText, { textAlign: rtl.textStart }]}>{getUserLabel(agent.linkedUserId)}</Text>
              ) : (
                <Text style={[styles.agentUnlinkedText, { textAlign: rtl.textStart }]}>{t('phoneSetup.notAssigned')}</Text>
              )}
            </View>
            <TouchableOpacity
              style={[styles.assignButton, agent.linkedUserId && styles.reassignButton]}
              onPress={() => startAssign(agent.extensionNumber)}
            >
              <Text style={[styles.assignButtonText, agent.linkedUserId && styles.reassignButtonText]}>
                {agent.linkedUserId ? t('phoneSetup.reassign') : t('phoneSetup.assign')}
              </Text>
            </TouchableOpacity>
          </View>
        ))
      )}
    </View>
  );

  if (!isAdminOrOwner) {
    return (
      <ScreenLayout
        header={<Header title={t('phoneSetup.headerTitle')} showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />}
        showBottomTabs={Platform.OS !== 'web'}
      >
        <View style={styles.accessDeniedContainer}>
          <Ionicons name="lock-closed" size={64} color={colors.text.secondary as string} />
          <Text style={styles.accessDeniedTitle}>{t('phoneSetup.accessDeniedTitle')}</Text>
          <Text style={styles.accessDeniedText}>{t('phoneSetup.accessDeniedText')}</Text>
          <TouchableOpacity style={styles.backButton} onPress={() => navigateTo('home')}>
            <Text style={styles.backButtonText}>{t('phoneSetup.goBack')}</Text>
          </TouchableOpacity>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={false}
          showMessages={false}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => setShowLeftPane(false)}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
    >
      <StepperReturnBanner />
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.heroContainer}>
          <View style={styles.phoneIconContainer}>
            <Ionicons name="call" size={48} color={colors.white as string} />
          </View>
          <Text style={styles.heroTitle}>{t('phoneSetup.heroTitle')}</Text>
          <Text style={styles.heroSubtitle}>{t('phoneSetup.heroSubtitle')}</Text>
        </View>

        {isLoading ? (
          <ActivityIndicator color={colors.primary as string} />
        ) : account ? (
          <>
            {renderConnected()}
            <View style={{ height: spacing.lg }} />
            {renderAgentsSection()}
          </>
        ) : (
          renderForm()
        )}

        {isEmbeddedInStepper && (
          <View style={styles.stepperFooter}>
            <TouchableOpacity
              style={styles.stepperContinueButton}
              onPress={() => navigateTo(onCompleteScreen, undefined, onCompleteParams)}
            >
              <Text style={styles.stepperContinueButtonText}>{t('auth.continue')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>

      <BusinessUserPicker
        visible={showUserPicker}
        onClose={() => {
          // In 'single' mode BusinessUserPicker calls onConfirm() then
          // immediately onClose() for the same pick, both within the same
          // event handler/render cycle — reading showPinModal here would see
          // its STALE pre-update value (React hasn't re-rendered yet), so a
          // synchronous ref (set inside handleUserPicked, before onClose
          // runs) is used instead of state to detect "a pick just happened".
          setShowUserPicker(false);
          if (!justPickedUserRef.current) {
            setAssigningExtension(null);
          }
          justPickedUserRef.current = false;
        }}
        onConfirm={handleUserPicked}
        mode="single"
        source="users"
        title={t('phoneSetup.assignExtensionTitle', { extension: assigningExtension ?? '' })}
      />

      <Modal
        visible={showPinModal}
        onClose={() => {
          if (isLinking) return;
          setShowPinModal(false);
          setAssigningExtension(null);
          setPendingUserId(null);
        }}
        title={t('phoneSetup.confirmPinTitle')}
        size="small"
      >
        <View style={styles.pinModalContent}>
          <Text style={[styles.pinModalText, { textAlign: rtl.textStart }]}>
            {t('phoneSetup.confirmPinText', {
              extension: assigningExtension ?? '',
              user: getUserLabel(pendingUserId) || t('phoneSetup.confirmPinThisUser'),
            })}
          </Text>
          <TextInput
            style={[styles.input, { textAlign: rtl.textStart }]}
            value={pinInput}
            onChangeText={setPinInput}
            placeholder={t('phoneSetup.pinPlaceholder')}
            placeholderTextColor={colors.text.secondary as string}
            keyboardType="number-pad"
            secureTextEntry
          />
          <TouchableOpacity
            style={[styles.connectButton, isLinking && styles.buttonDisabled]}
            onPress={handleConfirmLink}
            disabled={isLinking}
          >
            {isLinking ? (
              <ActivityIndicator color={colors.white as string} />
            ) : (
              <Text style={styles.connectButtonText}>{t('phoneSetup.confirmAndLink')}</Text>
            )}
          </TouchableOpacity>
        </View>
      </Modal>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  content: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },

  stepperFooter: { marginTop: spacing.xl },
  stepperContinueButton: {
    backgroundColor: colors.primary as string,
    borderRadius: 12,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  stepperContinueButtonText: {
    color: colors.white as string,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
  },

  heroContainer: { alignItems: 'center', marginBottom: spacing.xl },
  phoneIconContainer: {
    width: 80, height: 80, borderRadius: 40,
    backgroundColor: colors.primary as string,
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.md,
  },
  heroTitle: { fontSize: typography.fontSize['2xl'], fontFamily: typography.fontFamily.bold, color: colors.text.primary as string, marginBottom: spacing.sm },
  heroSubtitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, textAlign: 'center', color: colors.text.secondary as string },

  formCard: {
    backgroundColor: colors.background.surface as string, borderRadius: 12, padding: spacing.lg,
    borderWidth: 1, borderColor: colors.border as string,
  },
  formLabel: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string, marginBottom: spacing.xs, marginTop: spacing.sm },
  input: {
    borderWidth: 1, borderColor: colors.border as string, borderRadius: 8,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string,
    backgroundColor: colors.background.surface as string,
    marginBottom: spacing.sm,
  },

  sectionHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.md },
  sectionTitle: { fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string, marginBottom: spacing.xs },
  sectionSubtitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },

  emptyAgentsText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, fontStyle: 'italic' as const },

  agentRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border as string,
  },
  agentInfo: { flex: 1 },
  agentExtension: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  agentLinkedText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.status.success as string, marginTop: 2 },
  agentUnlinkedText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginTop: 2 },

  assignButton: {
    paddingHorizontal: spacing.md, paddingVertical: spacing.xs,
    borderRadius: 8, backgroundColor: colors.primary as string,
  },
  assignButtonText: { color: colors.white as string, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },
  reassignButton: { backgroundColor: colors.background.secondary as string },
  reassignButtonText: { color: colors.text.primary as string },

  connectButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: colors.primary as string, borderRadius: 10, padding: spacing.md,
    gap: spacing.sm, marginTop: spacing.md,
  },
  connectButtonText: { color: colors.white as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  buttonDisabled: { opacity: 0.6 },

  accountCard: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: colors.background.surface as string, borderRadius: 10, padding: spacing.md,
    borderWidth: 1, borderColor: colors.border as string,
  },
  accountInfo: { flexDirection: 'row', alignItems: 'center' },
  accountAvatar: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: colors.primary as string,
    justifyContent: 'center', alignItems: 'center',
  },
  accountName: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  accountSubtext: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  accountActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  statusDot: { width: 8, height: 8, borderRadius: 4 },

  pinModalContent: { padding: spacing.lg },
  pinModalText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginBottom: spacing.md },

  accessDeniedContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  accessDeniedTitle: { fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.bold, color: colors.text.primary as string, marginTop: spacing.md, marginBottom: spacing.sm },
  accessDeniedText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, textAlign: 'center', marginBottom: spacing.xl },
  backButton: { backgroundColor: colors.primary as string, paddingHorizontal: spacing.xl, paddingVertical: spacing.md, borderRadius: spacing.borderRadius.md },
  backButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.white as string },
});
